import {
  COOKIE_TRANSACTION_ID,
  DEFAULT_CONTEXT_TIMEOUT,
  DEFAULT_IDLE_TIMEOUT,
  DEFAULT_OUTLINK_SELECTOR,
  MAX_SEQUENCE_LENGTH,
  SESSION_PARAM,
} from './constants';
import {
  resolveGeoFromCookie,
  resolveGeoFromLegacyGlobal,
  resolveIpFromLegacyGlobal,
  resolveVisitorContext,
} from './context-resolver';
import {
  clearSession,
  getSessionIdFromCookie,
  getTransactionIdFromCookie,
  getURLParameter,
  getVisitorIdFromCookie,
  loadSession,
  saveSession,
  setSessionIdCookie,
  setTransactionIdCookie,
  setVisitorIdFallbackCookie,
} from './storage';
import { ActivityTracker } from './activity-tracker';
import { LinkDecorator } from './link-decorator';
import type {
  DecisionSessionPayload,
  PSSessionManager,
  SessionContext,
  SessionGeo,
  SessionManagerConfig,
  StoredSession,
} from './types';

const SESSION_ID_RE = /^[0-9a-f]{32}$/i;

function generateId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function isValidSessionId(id: string | null | undefined): id is string {
  return SESSION_ID_RE.test(id || '');
}

function nowISO(): string {
  return new Date().toISOString();
}

/**
 * SessionManager owns the full session lifecycle:
 * creation, resumption, idle expiration, activity tracking,
 * cross-domain link decoration, and exposing a shared context API.
 */
export class SessionManager implements PSSessionManager {
  readonly #config: SessionManagerConfig;
  #stored: StoredSession;
  #isNew: boolean;
  #visitorId: string | null;
  #activityTracker: ActivityTracker;
  #linkDecorator: LinkDecorator;
  #expiredCallbacks: Array<() => void> = [];
  #ipAddress: string | null = null;
  #geo: SessionGeo | null = null;
  #contextReady = false;

  constructor(config: Partial<SessionManagerConfig> = {}) {
    this.#config = {
      idleTimeout: config.idleTimeout ?? DEFAULT_IDLE_TIMEOUT,
      outlinkSelector: config.outlinkSelector ?? DEFAULT_OUTLINK_SELECTOR,
      sessionParam: config.sessionParam ?? SESSION_PARAM,
      cookieDomain: config.cookieDomain,
      geoEndpoint: config.geoEndpoint,
      contextTimeout: config.contextTimeout ?? DEFAULT_CONTEXT_TIMEOUT,
    };

    const resolved = this.#resolveSession();
    this.#stored = resolved.session;
    this.#isNew = resolved.isNew;
    this.#visitorId = this.#resolveVisitorId();

    // Sync geo resolution — precedence: PS_GEO > mf_geo cookie > window.MF.* (legacy CMS)
    const ssrGeo = (window as any).PS_GEO;
    if (ssrGeo && typeof ssrGeo === 'object' && typeof ssrGeo.country === 'string') {
      this.#geo = ssrGeo as SessionGeo;
      this.#contextReady = true;
    } else {
      const cookieGeo = resolveGeoFromCookie();
      if (cookieGeo) {
        this.#geo = cookieGeo.geo;
        this.#ipAddress = cookieGeo.ipAddress;
        this.#contextReady = true;
      }
    }

    // Last sync fallback: legacy cms-macros globals (window.MF.geoData + window.MF.ipAddress).
    // If geo is resolved here, mark contextReady so the async geoEndpoint does not fire.
    // If only IP is resolved, leave contextReady false so async can still populate geo.
    if (!this.#geo) {
      const legacyGeo = resolveGeoFromLegacyGlobal();
      if (legacyGeo) {
        this.#geo = legacyGeo;
        this.#contextReady = true;
      }
    }
    if (!this.#ipAddress) {
      this.#ipAddress = resolveIpFromLegacyGlobal();
    }

    // Persist immediately
    this.#persist();

    // Record current page in sequence
    this.recordPageVisit(window.location.href);

    // Activity tracking
    this.#activityTracker = new ActivityTracker(() => this.touch());
    this.#activityTracker.start();

    // Link decoration
    this.#linkDecorator = new LinkDecorator(
      this.#config.sessionParam,
      this.#stored.id,
      this.#config.outlinkSelector,
    );

    // Fire-and-forget: resolve IP + geo context asynchronously
    this.#resolveContextAsync();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  get contextReady(): boolean {
    return this.#contextReady;
  }

  get context(): SessionContext {
    return {
      id: this.#stored.id,
      startedAt: this.#stored.startedAt,
      lastActivityAt: this.#stored.lastActivityAt,
      initialUrl: this.#stored.initialUrl,
      currentUrl: window.location.href,
      referrerUrl: this.#stored.referrerUrl,
      pageSequence: [...this.#stored.pageSequence],
      eventSequence: [...this.#stored.eventSequence],
      isNew: this.#isNew,
      transactionId: this.#stored.transactionId,
      visitorId: this.#visitorId,
      ipAddress: this.#ipAddress,
      geo: this.#geo,
    };
  }

  recordPageVisit(url?: string): void {
    const page = url ?? window.location.href;
    if (this.#stored.pageSequence.length < MAX_SEQUENCE_LENGTH) {
      this.#stored.pageSequence.push(page);
    }
    this.touch();
  }

  recordEvent(eventName: string): void {
    if (this.#stored.eventSequence.length < MAX_SEQUENCE_LENGTH) {
      this.#stored.eventSequence.push(eventName);
    }
    this.touch();
  }

  touch(): void {
    this.#stored.lastActivityAt = nowISO();
    this.#persist();
  }

  toDecisionPayload(
    pageId: string,
    pageListId: string,
  ): DecisionSessionPayload & { page_id: string; page_list_id: string } {
    return {
      session_id: this.#stored.id,
      started_at: this.#stored.startedAt,
      last_activity_at: this.#stored.lastActivityAt,
      initial_url: this.#stored.initialUrl,
      current_url: window.location.href,
      referrer_url: this.#stored.referrerUrl,
      page_sequence: [...this.#stored.pageSequence],
      event_sequence: [...this.#stored.eventSequence],
      requested_at: nowISO(),
      page_id: pageId,
      page_list_id: pageListId,
    };
  }

  onExpired(cb: () => void): void {
    this.#expiredCallbacks.push(cb);
  }

  /** Decorate outlinks and start observing for new ones. Call after DOM is ready. */
  initLinks(): void {
    this.#linkDecorator.decorateAll();
    this.#linkDecorator.observe();
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------

  /**
   * Resolve IP + geo context asynchronously. Sets contextReady = true and
   * dispatches ps:context-ready even on failure (with null values).
   * Called fire-and-forget from the constructor.
   */
  async #resolveContextAsync(): Promise<void> {
    try {
      if (!this.#contextReady && this.#config.geoEndpoint) {
        const result = await resolveVisitorContext(
          this.#config.geoEndpoint,
          this.#config.contextTimeout,
        );
        this.#ipAddress = result.ipAddress;
        this.#geo = result.geo;
      } else {
        // Yield to ensure the event fires after the constructor returns
        // and window.PS.session is assigned (consumers depend on it).
        await Promise.resolve();
      }
    } catch {
      // Ensure we always proceed — nulls are acceptable
    } finally {
      this.#contextReady = true;
      window.dispatchEvent(new CustomEvent('ps:context-ready'));
    }
  }

  /**
   * Resolve the session: resume an existing one, pick up from URL param,
   * or create a new one. Handles idle expiration.
   */
  #resolveSession(): { session: StoredSession; isNew: boolean } {
    // 1. Check URL param (cross-domain handoff)
    const fromUrl = getURLParameter(this.#config.sessionParam);
    if (fromUrl && isValidSessionId(fromUrl)) {
      // Cross-domain arrival — try to resume stored state if ID matches,
      // otherwise start fresh with the handed-off ID.
      const stored = loadSession();
      if (stored && stored.id === fromUrl && !this.#isExpired(stored)) {
        stored.lastActivityAt = nowISO();
        return { session: stored, isNew: false };
      }
      return { session: this.#createSession(fromUrl), isNew: true };
    }

    // 2. Check cookie + localStorage
    const cookieId = getSessionIdFromCookie();
    if (cookieId && isValidSessionId(cookieId)) {
      const stored = loadSession();
      if (stored && stored.id === cookieId && !this.#isExpired(stored)) {
        stored.lastActivityAt = nowISO();
        return { session: stored, isNew: false };
      }
      // Cookie exists but localStorage lost or expired — treat as expired
    }

    // 3. Create new session
    return { session: this.#createSession(), isNew: true };
  }

  #createSession(id?: string): StoredSession {
    const now = nowISO();
    const transactionId = this.#resolveTransactionId();

    // Fire expired callbacks if replacing an existing session
    const old = loadSession();
    if (old) {
      clearSession();
      for (const cb of this.#expiredCallbacks) {
        try { cb(); } catch { /* ignore */ }
      }
    }

    return {
      id: id ?? generateId(),
      startedAt: now,
      lastActivityAt: now,
      initialUrl: window.location.href,
      referrerUrl: document.referrer,
      pageSequence: [],
      eventSequence: [],
      transactionId,
    };
  }

  /**
   * Resolve visitor ID per ADR-0015. Normally the mf_vid cookie is server-set
   * by a CloudFront Function (first-party, 1yr TTL). If not present (non-owned
   * domain), fall back to a JS-set cookie (Safari ITP caps to 7 days).
   */
  #resolveVisitorId(): string | null {
    const existing = getVisitorIdFromCookie();
    if (existing) return existing;

    // Fallback: generate client-side and set as JS cookie
    const id = generateId();
    setVisitorIdFallbackCookie(id, this.#config.cookieDomain);
    return id;
  }

  #resolveTransactionId(): string | null {
    const fromCookie = getTransactionIdFromCookie();
    if (fromCookie) return fromCookie;

    const fromUrl = getURLParameter(COOKIE_TRANSACTION_ID);
    if (fromUrl) {
      setTransactionIdCookie(fromUrl, this.#config.idleTimeout, this.#config.cookieDomain);
      return fromUrl;
    }
    return null;
  }

  #isExpired(stored: StoredSession): boolean {
    const lastActivity = new Date(stored.lastActivityAt).getTime();
    if (isNaN(lastActivity)) return true;
    const elapsed = (Date.now() - lastActivity) / 1000;
    return elapsed > this.#config.idleTimeout;
  }

  #persist(): void {
    saveSession(this.#stored);
    setSessionIdCookie(this.#stored.id, this.#config.idleTimeout, this.#config.cookieDomain);
  }
}
